import type { ITreeSelectNode } from "./kuika-tree-select";

/** A node, and the node it hangs from (`null` at the top level). */
export interface ITreeSelectIndexEntry {
  node: ITreeSelectNode;
  parentId: string | null;
}

/** Every node of a tree, reachable by `id` and by `path` without walking the tree again. */
export interface ITreeSelectIndex {
  byId: Map<string, ITreeSelectIndexEntry>;
  /** The first node, in tree order, whose `path` is the key — a value resolves to one row. */
  idByPath: Map<string, string>;
}

/** What a search term leaves of a tree. */
export interface ITreeSelectSearchResult {
  /**
   * Every node the term matches, with its whole subtree (a matched folder can still be opened to
   * see what is in it), and every ancestor that leads to a match. Nothing else.
   */
  nodes: ITreeSelectNode[];
  /** The ancestors of every match — opened, so that no match is hidden inside a closed folder. */
  expandedIds: string[];
  /** The first match, in tree order, that can be picked (not `disabled`), or null when none can. */
  firstEnabledMatchId: string | null;
}

interface ISearchVisit {
  node: ITreeSelectNode;
  /** Whether this node or anything below it matched the term. */
  containsMatch: boolean;
}

export const hasTreeSelectChildren = (node: ITreeSelectNode): boolean =>
  node.children !== undefined && node.children.length > 0;

export const buildTreeSelectIndex = (nodes: ITreeSelectNode[]): ITreeSelectIndex => {
  const byId: Map<string, ITreeSelectIndexEntry> = new Map<string, ITreeSelectIndexEntry>();
  const idByPath: Map<string, string> = new Map<string, string>();

  const visit = (node: ITreeSelectNode, parentId: string | null): void => {
    byId.set(node.id, { node, parentId });
    if (!idByPath.has(node.path)) {
      idByPath.set(node.path, node.id);
    }
    (node.children ?? []).forEach((child: ITreeSelectNode): void => visit(child, node.id));
  };
  nodes.forEach((node: ITreeSelectNode): void => visit(node, null));

  return { byId, idByPath };
};

/** Every node that can be opened — what "expand all" opens. */
export const collectExpandableTreeSelectIds = (nodes: ITreeSelectNode[]): string[] => {
  const ids: string[] = [];
  const visit = (node: ITreeSelectNode): void => {
    if (!hasTreeSelectChildren(node)) return;
    ids.push(node.id);
    (node.children ?? []).forEach(visit);
  };
  nodes.forEach(visit);
  return ids;
};

/** Whether every ancestor of the node is open, so its row is on screen. */
export const isTreeSelectNodeVisible = (
  index: ITreeSelectIndex,
  nodeId: string,
  expandedIds: ReadonlySet<string>
): boolean => {
  let parentId: string | null = index.byId.get(nodeId)?.parentId ?? null;
  while (parentId !== null) {
    if (!expandedIds.has(parentId)) return false;
    parentId = index.byId.get(parentId)?.parentId ?? null;
  }
  return index.byId.has(nodeId);
};

/**
 * Cut a tree down to what a search term reaches. A node matches when its `name` or its `path`
 * contains the term, case-insensitively — the path, so that a nested field is found by the name of
 * the action it belongs to, as the antd TreeSelect this replaced found it (it searched the value).
 *
 * `toLowerCase`, never `toLocaleLowerCase`: under a Turkish locale the locale-aware form lowers "I"
 * to "ı", and a search for "id" would stop finding "ID".
 */
export const searchTreeSelectNodes = (nodes: ITreeSelectNode[], term: string): ITreeSelectSearchResult => {
  const needle: string = term.toLowerCase();
  const expandedIds: string[] = [];
  let firstEnabledMatchId: string | null = null;

  const matches = (node: ITreeSelectNode): boolean =>
    node.name.toLowerCase().includes(needle) || node.path.toLowerCase().includes(needle);

  // Pre-order, so the first match recorded is the first one a reader meets going down the list.
  const visit = (node: ITreeSelectNode, insideMatch: boolean): ISearchVisit | null => {
    const isMatch: boolean = matches(node);
    if (isMatch && firstEnabledMatchId === null && node.disabled !== true) {
      firstEnabledMatchId = node.id;
    }

    const children: ITreeSelectNode[] = [];
    let childContainsMatch: boolean = false;
    for (const child of node.children ?? []) {
      const visited: ISearchVisit | null = visit(child, insideMatch || isMatch);
      if (visited === null) continue;
      children.push(visited.node);
      childContainsMatch = childContainsMatch || visited.containsMatch;
    }

    if (!isMatch && !insideMatch && !childContainsMatch) return null;
    if (childContainsMatch) {
      expandedIds.push(node.id);
    }
    const kept: ITreeSelectNode = node.children === undefined ? node : { ...node, children };
    return { node: kept, containsMatch: isMatch || childContainsMatch };
  };

  const kept: ITreeSelectNode[] = [];
  for (const node of nodes) {
    const visited: ISearchVisit | null = visit(node, false);
    if (visited !== null) kept.push(visited.node);
  }

  return { nodes: kept, expandedIds, firstEnabledMatchId };
};
